import PropTypes from 'prop-types';
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { isAfter } from 'date-fns';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';

import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import { userVirtualPageView as gtmUserVirtualPageView, userForgotPassword as gtmUserForgotPassword } from '@xanadu/ui-shared/interfaces/googleTagManager/actions';

import fetchService, { sanitizeErrorMessage } from '@xanadu/ui-shared/utils/fetchService';
import Button from '@xanadu/ui-shared/components/widgets/Button';

import validationUtil from '../SignUp/validation';

import TrustPilotWidget from '../common/TrustPilotWidget';
import PartnerLogos from '../common/PartnerLogos';
import InputField from '../widgets/InputField';

import IconClose from '../../../images/icons/misc/icon-close.svg';

import styles from './ResetPassword.module.scss';
import './transitionGroup.scss';

export const PASSWORD_FIELDS = {
  name: 'password',
  autoComplete: 'new-password',
  dataHook: 'register-password',
  label: 'Password',
  error: '',
  value: '',
  section: 'account',
  isRequired: true
};

const USERNAME_FIELDS = {
  name: 'username',
  type: 'text',
  error: '',
  label: 'Username',
  value: ''
};

const ResetPassword = ({
  widthSize,
  updatePasswordRequest,
  success,
  onRequestClose,
  loading,
  error,
  loginRequest,
  params
}) => {
  const isMobile = widthSize < 1024;
  const [requestResetStatus, setRequestResetStatus] = useState('default');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [passwordState, setPasswordState] = useState({ ...PASSWORD_FIELDS });
  const [usernameState, setUsernameState] = useState({ ...USERNAME_FIELDS });

  const trustPilotRef = useRef();
  const trustPilotContainerEl = useRef();
  const footerEl = useRef();

  const tokenIsInvalid = error.includes('Reset token has already been used.');

  const expired = isAfter(new Date(), new Date(params.expiry))
    || (error && error.includes('token has expired.'))
    || tokenIsInvalid;

  const debounceValidation = useMemo(() => debounce(({ password }) => {
    const errorResult = validationUtil('password', password);

    setPasswordState(prevState => ({
      ...prevState,
      value: password,
      error: errorResult || '',
    }));
  }, 400), []);

  const onPropertyChange = useCallback(({ name, value }) => {
    if (name === 'password') {
      setPasswordState(prevState => ({
        ...prevState,
        value,
      }));

      debounceValidation({ password: value });
      return;
    }

    setUsernameState(prevState => ({
      ...prevState,
      value,
    }));
  }, []);

  const onInputBlur = useCallback(({ name, value }) => {
    if (name === 'password') {
      const errorResult = validationUtil('password', value);

      setPasswordState(prevState => ({
        ...prevState,
        value,
        error: errorResult || '',
      }));

      return;
    }

    setUsernameState(prevState => ({
      ...prevState,
      value,
    }));
  }, []);

  const resetPasswordRequest = async () => {
    setRequestResetStatus('processing');

    const { value: username } = usernameState;
    const url = '/bpapi/rest/security/v2/password-reset';
    const options = {
      method: 'POST',
      body: JSON.stringify({ username }),
    };

    try {
      const response = await fetchService(url, options);
      const { errors } = response || {};

      if (!errors) {
        setRequestResetStatus('done');
        setResetPasswordError('');

        // NOTE: We need to trigger the GTM event to send some data to dataLayerPush()
        gtmDispatch(gtmUserForgotPassword({ userForgot: username }));

        setTimeout(() => {
          onRequestClose();
        }, 2000);
        return;
      }

      setRequestResetStatus('failed');
      setResetPasswordError(sanitizeErrorMessage(errors));
    } catch (err) {
      const errorMessage = 'Error requesting new password reset link.';
      // eslint-disable-next-line no-console
      setRequestResetStatus('failed');
      setResetPasswordError(errorMessage);
    }
  };

  const handleClick = () => {
    if (success) {
      gtmDispatch(gtmUserVirtualPageView({
        currentUrl: window.location.href,
        eventName: 'Login Request',
        virtualPageTitle: 'Reset Password',
        virtualPageURL: '/update-password/',
        previousURL: `${location.href}`,
        clickSource: { clickSource: `reset-password-button_${location.href}` }
      }));

      onRequestClose();

      setTimeout(() => {
        loginRequest();
      }, 600);

      return;
    }

    if (expired) {
      resetPasswordRequest();
      return;
    }

    updatePasswordRequest({
      username: params.username,
      token: params.token,
      password: passwordState.value
    });
  };

  useEffect(() => {
    const timeoutAnimation = isMobile ? 330 : 420;
    document.documentElement.style.overflow = 'hidden';

    setTimeout(() => {
      if (trustPilotContainerEl.current && footerEl.current) {
        trustPilotContainerEl.current.classList.add(styles['trustPilotBox--show']);
        footerEl.current.classList.add(styles['footer--show']);
      }
    }, timeoutAnimation);

    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(trustPilotRef.current, true);
    }

    return () => {
      document.documentElement.style.overflow = '';
      setPasswordState({ ...PASSWORD_FIELDS });
    };
  }, []);

  const buttonDisabled = expired
    ? !usernameState.value
    : (!passwordState.value || !!passwordState.error);

  const onFormSubmit = (e) => {
    e.preventDefault();

    if (!buttonDisabled) {
      handleClick();
    }
  };

  const animationClass = 'sliding-normal';
  const mainClass = cn('ResetPassword', styles.main);

  let transitionKey = 'reset-password';
  let buttonText = 'Next';
  let content = (
    <div className={styles.formContent} key={transitionKey}>
      <div className={styles.text}>
        {'Enter a new password below. If your account has been password locked, your account will be unlocked.'}
      </div>
      <InputField
        focusOnMount
        label={passwordState.label}
        name={passwordState.name}
        dataHook={passwordState.dataHook}
        type="password"
        value={passwordState.value}
        onPropertyChange={onPropertyChange}
        onInputBlur={onInputBlur}
        error={passwordState.error}
        errorAlign={isMobile ? 'left' : 'right'}
        className={styles.inputField}
        placeholder="Minimum length 8"
        inline={!isMobile}
        fullWidth={!!isMobile}
        isMobile={isMobile}
      />
    </div>
  );

  if (expired) {
    buttonText = 'Send Link';
    content = (
      <div className={styles.formContent} key={transitionKey}>
        <div className={styles.text}>
          <p>{'The link you have used is invalid. It may have expired or have been used already.'}</p>
          <p>{'To request a new link, please enter your Username below. An email will be sent to your registered email address.'}</p>
        </div>
        <InputField
          focusOnMount
          label={usernameState.label}
          name={usernameState.name}
          dataHook={usernameState.dataHook}
          type="text"
          value={usernameState.value}
          onPropertyChange={onPropertyChange}
          onInputBlur={onInputBlur}
          error={usernameState.error}
          errorAlign={isMobile ? 'left' : 'right'}
          className={styles.inputField}
          placeholder="Username"
          inline={!isMobile}
          fullWidth={!!isMobile}
          isMobile={isMobile}
        />
      </div>
    );
  }

  if (!tokenIsInvalid && (error || resetPasswordError)) {
    buttonText = 'Try Again';
  }

  if (success) {
    buttonText = 'Log In';
    transitionKey = 'reset-password-success';
    content = (
      <div className={styles.formContent} key={transitionKey}>
        <div className={styles.text}>
          <p>Your password has been reset, and your account has been unlocked.</p>
          <p>You may now log in to your account. Thank you for using Matchbook.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={mainClass}>
      <Link className={styles.logo} to="/" />
      <a
        role="button"
        data-hook="forgot-password-close"
        className={styles.closeButton}
        onClick={onRequestClose}
      >
        <span>Close</span>
        <IconClose />
      </a>
      <div className={styles.content}>
        <form className={styles.formContainer} onSubmit={onFormSubmit}>
          <div className={styles.header}>
            {expired ? 'Invalid Link' : 'Reset Your Password & Unlock Account'}
          </div>
          <SwitchTransition>
            <CSSTransition
              key={transitionKey}
              timeout={600}
              classNames={animationClass}
            >
              {content}
            </CSSTransition>
          </SwitchTransition>
          <Button
            onClick={handleClick}
            sizeType="wider"
            buttonType="primary"
            disabled={buttonDisabled}
            loading={loading || requestResetStatus === 'processing'}
            success={requestResetStatus === 'done'}
            className={styles.nextButton}
            dataHook="reset-password-button"
          >
            {buttonText}
          </Button>
          {!tokenIsInvalid && (!!error || !!resetPasswordError) && (
            <span className={styles.mainErrorMessage}>
              {error || resetPasswordError}
            </span>
          )}
          {isMobile && (
            <div className={styles.footer} ref={footerEl}>
              <PartnerLogos />
            </div>
          )}
        </form>
        {!isMobile && (
          <div className={styles.footer} ref={footerEl}>
            <PartnerLogos />
          </div>
        )}
      </div>
      {!isMobile && (
        <div className={styles.trustPilotBox} ref={trustPilotContainerEl}>
          <span>{"The UK's most trusted Betting Exchange"}</span>
          <TrustPilotWidget trustBoxRef={trustPilotRef} />
        </div>
      )}
    </div>
  );
};

ResetPassword.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  updatePasswordRequest: PropTypes.func.isRequired,
  widthSize: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  error: PropTypes.PropTypes.string,
  loginRequest: PropTypes.func.isRequired,
  params: PropTypes.instanceOf(Object).isRequired,
};

export default ResetPassword;
